import { ApiResponse } from 'apisauce';
import { network } from './network';

export type AgentAccess = 'view' | 'edit';

export type AgentConsentRequestDto = {
  requestId: string;
  clientName: string;
  clientHost: string;
  logoUri?: string | null;
};

export type AgentConnectionDto = {
  id: number;
  clientName: string;
  clientHost: string;
  logoUri?: string | null;
  access: AgentAccess;
  createdAt: string;
  lastUsedAt?: string | null;
};

export type AgentUndoKind = 'TASK_UNDO' | 'PLAN_UNDO' | 'EVENT_RESTORE';

export type AgentActivityDto = {
  id: number;
  clientName: string;
  semesterId?: number | null;
  action: string;
  delete: boolean;
  summary: string;
  createdAt: string;
  undoneAt?: string | null;
  canUndo: boolean;
  undoKind?: AgentUndoKind | null;
  undoPayload?: any;
};

async function getActivity(): Promise<ApiResponse<AgentActivityDto[]>> {
  return network.get('/agent-access/activity');
}

async function markUndone(id: number): Promise<ApiResponse<AgentActivityDto>> {
  return network.post(`/agent-access/activity/${id}/undone`);
}

// When an AI app last changed something, so an open tab can reload.
export type AgentLastChangeDto = {
  connected: boolean;
  lastChangeAt?: string | null;
  // An AI app that starts a new term makes it current: a tab on the old one switches.
  currentSemesterId?: number | null;
};

async function getLastChange(): Promise<ApiResponse<AgentLastChangeDto>> {
  return network.get('/agent-access/last-change');
}

async function getConsentRequest(requestId: string): Promise<ApiResponse<AgentConsentRequestDto>> {
  return network.get(`/agent-access/requests/${encodeURIComponent(requestId)}`);
}

async function approve(requestId: string, access: AgentAccess): Promise<ApiResponse<{ redirectUrl: string }>> {
  return network.post(`/agent-access/requests/${encodeURIComponent(requestId)}/approve`, { access });
}

async function deny(requestId: string): Promise<ApiResponse<{ redirectUrl: string }>> {
  return network.post(`/agent-access/requests/${encodeURIComponent(requestId)}/deny`);
}

async function getConnections(): Promise<ApiResponse<AgentConnectionDto[]>> {
  return network.get('/agent-access/connections');
}

async function updateConnection(id: number, access: AgentAccess): Promise<ApiResponse<AgentConnectionDto>> {
  return network.patch(`/agent-access/connections/${id}`, { access });
}

async function disconnect(id: number): Promise<ApiResponse<void>> {
  return network.delete(`/agent-access/connections/${id}`);
}

export default {
  getConsentRequest,
  approve,
  deny,
  getConnections,
  updateConnection,
  disconnect,
  getActivity,
  markUndone,
  getLastChange
};
